import { Button, Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  DIAGNOSTIC_CODE_CATALOG_INITIAL_VALUES,
  diagnosticCodeCatalogInitialValuesType,
} from "../../../../../constants/initial-schemas";
import { DIAGNOSTIC_CODE_TYPES } from "../../../../../constants/master";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useLoading } from "../../../../../hooks/use-loading";
import { useAddCataLogCode } from "../../../../../queries/catalog/use-add-catalog-code";
import { useUpdateCataLogCode } from "../../../../../queries/catalog/use-update-catalog-code";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../../utils/utils";
import CustomSelect from "../../../../CustomSelect";
import Input from "../../../../Input";
import LoadingOverlay from "../../../../LoadingOverlay";
import SideSheetDrawer from "../../../../SidesheetDrawer";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
interface DiagnosticCodeCatalogDialogProps {
  dialogTitle: string;
  disclosureHost: DisclosureHost;
  preFillValues?: diagnosticCodeCatalogInitialValuesType;
  uuid?: string;
}

const DiagnosticCodeValidationSchema = Yup.object({
  codeVersion: Yup.string().required().label("ICD Code Type"),
  icd_code: Yup.string().trim().required().label("ICD Code"),
  description: Yup.string().trim().required().label("Description"),
});

const DiagnosisCodeCatalogDialog = ({
  dialogTitle,
  disclosureHost,
  preFillValues,
  uuid,
}: DiagnosticCodeCatalogDialogProps) => {
  const {
    values,
    handleSubmit,
    handleBlur,
    handleChange,
    resetForm,
    setFieldValue,
    errors,
    touched,
  } = useFormik({
    initialValues: preFillValues ?? DIAGNOSTIC_CODE_CATALOG_INITIAL_VALUES,
    validationSchema: DiagnosticCodeValidationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const { mutateAsync: addDiagnosisCode } = useAddCataLogCode();
  const { mutateAsync: editDiagnosisCode } = useUpdateCataLogCode();
  const queryClient = useQueryClient();

  const showSnackbar = useSnackbar();
  const [isLoading, setIsLoading] = useLoading();

  function resetAndClose() {
    resetForm();
    disclosureHost.close();
  }

  function onSubmit(values: diagnosticCodeCatalogInitialValuesType) {
    setIsLoading(true);

    const transformedValues = {
      code: values.icd_code.trim(),
      description: values.description.trim(),
      codeVersion: values.codeVersion,
    };

    (preFillValues
      ? editDiagnosisCode({
          values: {
            ...transformedValues,
            uuid,
            type: "ICD",
          },
        })
      : addDiagnosisCode({ values: { ...transformedValues, type: "ICD" } })
    )
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["catalog-diagnosis-code-import"],
        });

        resetAndClose();
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      })
      .finally(() => setIsLoading(false));
  }

  return (
    <SideSheetDrawer
      open={disclosureHost.opened}
      title={dialogTitle}
      onClose={resetAndClose}
      w="50vw"
      footerActions={
        <Button
          sx={{ width: "105px" }}
          onClick={() => handleSubmit()}
          variant="contained"
        >
          {dialogTitle === "Edit Diagnosis Code" ? "Edit" : "Add"}
        </Button>
      }
    >
      <LoadingOverlay isLoading={isLoading} />

      <Grid container spacing={2} width="100%">
        <Grid item xs={6}>
          <CustomSelect
            required
            value={values.codeVersion}
            onChange={(val) => setFieldValue("codeVersion", val)}
            label="ICD Code Type"
            options={DIAGNOSTIC_CODE_TYPES}
            placeholder="Select"
            error={touched.codeVersion && errors.codeVersion}
          />
        </Grid>
        <Grid item xs={6}>
          <Input
            required
            label="ICD Code"
            textFieldProps={{
              name: "icd_code",
              value: values.icd_code,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Enter Code",
            }}
            error={touched.icd_code && errors.icd_code}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            required
            label="Description"
            textFieldProps={{
              name: "description",
              value: values.description,
              onChange: handleChange,
              onBlur: handleBlur,
              placeholder: "Description",
              multiline: true,
              rows: 4,
            }}
            error={touched.description && errors.description}
          />
        </Grid>
      </Grid>
    </SideSheetDrawer>
  );
};
export default DiagnosisCodeCatalogDialog;
